import { useEffect, useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';

import { ASSET_NAME, featuredListingsSelector } from '../LandingPage/LandingPage.duck';
import { useIntl } from 'react-intl';
import { NamedRedirect } from '../../components';
import { fetchLiveBidsListings, liveBidsListingsSelector, liveBidsInProgressSelector, liveBidsErrorSelector } from '../../components/LiveBidsCarousel/LiveBidsCarousel.duck';
import LiveBidsCarouselContainer from '../LandingPage/LiveBidsCarouselContainer';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const EnglishLandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    featuredListings,
    liveBidsListings,
    liveBidsInProgress,
    liveBidsError,
    onFetchLiveBidsListings,
  } = props;

  // If current locale is Dutch, do not stay on English landing.
  // Redirect to root '/' (LandingPage)
  const intl = useIntl();
  const isDutch = (intl?.locale || '').toLowerCase().startsWith('nl');
  if (isDutch) {
    return <NamedRedirect name="LandingPage" />;
  }

  // Fetch live bids listings once on component mount (only if not already cached)
  useEffect(() => {
    // Data will be fetched by the container component based on cache status
  }, []);

  // Refresh live bids when page regains focus (only if data is stale)
  useEffect(() => {
    const handleFocus = () => {
      // Refresh will be handled by container component
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Memoize options to prevent creating new object reference on every render
  const options = useMemo(() => ({
    featuredListings: featuredListings,
    liveBidsListings: liveBidsListings,
    liveBidsInProgress: liveBidsInProgress,
    liveBidsError: liveBidsError,
  }), [featuredListings, liveBidsListings, liveBidsInProgress, liveBidsError]);

  return (
    <>
      <PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
        options={options}
      />
      {/* <LiveBidsCarouselContainer /> */}
    </>
  );
};

EnglishLandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};

  const featuredListings = featuredListingsSelector(state);
  const liveBidsListings = liveBidsListingsSelector(state);
  const liveBidsInProgress = liveBidsInProgressSelector(state);
  const liveBidsError = liveBidsErrorSelector(state);

  return { pageAssetsData, inProgress, error, featuredListings, liveBidsListings, liveBidsInProgress, liveBidsError };
};

const mapDispatchToProps = dispatch => ({
  onFetchLiveBidsListings: () => dispatch(fetchLiveBidsListings()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const EnglishLandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnglishLandingPageComponent);

export default EnglishLandingPage;
